import React, { memo, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { formatCicDateTime } from 'utils/cicReason';

import {
  CicNextDisclosureTitle,
  FEE_CHANGES_TITLE,
  NEXT_DISCLOSURE_FALLBACK_TITLE,
  NO_PENDING_CHANGES_MESSAGE,
} from '../../LoanCicHistory.constants';
import { cicChangePropTypes, cicFeeChangePropTypes } from '../../LoanCicHistory.propTypes';
import { LoanCicHistoryTestIds } from '../../LoanCicHistory.testIds';
import { getPendingFootnote, getWindowLabel } from '../../LoanCicHistory.utils';
import CicChangesTable from '../CicChangesTable';
import CicEmptyMessage from '../CicEmptyMessage';
import CicFeeChangesTable from '../CicFeeChangesTable';
import CicSectionCard from '../CicSectionCard';

import { PENDING_CHIP } from './CicPendingCard.constants';
import { windowStyles } from './CicPendingCard.styles';

const CicPendingCard = ({ pending, isEditAllowed, onEdit, onEditFeeChange, onOpenHistory }) => {
  const { bundleType, plannedTriggerDate, changes, feeChanges } = pending;

  const hasWindow = !_.isNil(plannedTriggerDate);
  const hasChanges = !_.isEmpty(changes);
  const hasFeeChanges = !_.isEmpty(feeChanges);

  const windowContent = useMemo(
    () =>
      hasWindow && (
        <Box data-testid={LoanCicHistoryTestIds.PENDING_WINDOW_LABEL} sx={windowStyles}>
          <Typography color="text.secondary" variant="regular14">
            {getWindowLabel(plannedTriggerDate)}
          </Typography>
          <Typography color="warning.main" variant="medium14">
            {formatCicDateTime(plannedTriggerDate)}
          </Typography>
        </Box>
      ),
    [hasWindow, plannedTriggerDate],
  );

  const footnote = useMemo(
    () =>
      hasWindow && (
        <Typography color="text.secondary" variant="regular12">
          {getPendingFootnote(bundleType)}
        </Typography>
      ),
    [hasWindow, bundleType],
  );

  return (
    <CicSectionCard
      isHighlighted
      chip={PENDING_CHIP}
      dataTestId={LoanCicHistoryTestIds.PENDING_CARD}
      headerAside={footnote}
      headerContent={windowContent}
      title={CicNextDisclosureTitle[bundleType] ?? NEXT_DISCLOSURE_FALLBACK_TITLE}
    >
      {!hasChanges && !hasFeeChanges && <CicEmptyMessage message={NO_PENDING_CHANGES_MESSAGE} />}
      {hasChanges && (
        <CicChangesTable
          hasActions
          changes={changes}
          isEditAllowed={isEditAllowed}
          onEdit={onEdit}
          onOpenHistory={onOpenHistory}
        />
      )}
      {hasFeeChanges && (
        <CicFeeChangesTable
          dataTestId={LoanCicHistoryTestIds.PENDING_FEE_CHANGES}
          feeChanges={feeChanges}
          isEditAllowed={isEditAllowed}
          title={FEE_CHANGES_TITLE}
          onEdit={onEditFeeChange}
        />
      )}
    </CicSectionCard>
  );
};

CicPendingCard.propTypes = {
  isEditAllowed: PropTypes.bool.isRequired,
  pending: PropTypes.shape({
    bundleType: PropTypes.string,
    plannedTriggerDate: PropTypes.string,
    changes: PropTypes.arrayOf(cicChangePropTypes),
    feeChanges: PropTypes.arrayOf(cicFeeChangePropTypes),
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onEditFeeChange: PropTypes.func.isRequired,
  onOpenHistory: PropTypes.func.isRequired,
};

export default memo(CicPendingCard);
